import { useState, useEffect } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { round } from "lodash";
import { useToasts } from "react-toast-notifications";
import { useSelector } from "react-redux";

import { ReactComponent as AddIcon } from "@/assets/images/desktop/agregar.svg";
import { useAppOrderCreate } from "@/hooks/planning/application-order/applicationOrders";
import { useProducts } from "@/hooks/products/useProducts";
import { formatMiles, formatMilesDecimalNew } from "@/services/Utils";
import { getProductLackDays } from "@/api/Products/products-api";

import ProductSelectorDB from "@/components/desktop/products/product_selector_db/ProductSelectorDB";
import Fetching from "@/components/Fetching/Fetching";

import { baseItem } from "./createOrderApplication.constants";
import CreateAppOrderTableItem from "./CreateOrderApplicationTableItem";
import CreateAppOrderTableHead from "./CreateOrderApplicationTableHead";

import classes from "./createOrderApplication.module.scss";

const CreateAppOrderTableDetail = ({ params, data, setData, calculateTotal }) => {
	const { estate_parent_id, estate_id } = useSelector((state) => state.auth.filter);

	const { data: response } = useAppOrderCreate(params);
	const { data: responseProducts, isLoading: loadingProducts } = useProducts({
		estate_parent_id,
		estate_id,
		get_generics: 1,
		get_stock: 1,
		get_all: data.all_product,
		get_active_ingredients: 1,
	});

	const { addToast } = useToasts();

	const [activeProduct, setActiveProduct] = useState(false);
	const [products, setProducts] = useState([]);
	const [objectives, setObjectives] = useState([]);

	useEffect(() => {
		let newProducts = [];

		if (responseProducts) {
			newProducts = responseProducts.products;
		}

		if (data.estate_id && responseProducts) {
			newProducts = newProducts.filter((p) => String(p.estate_id) === String(data.estate_id) || p.estate_id === null);
		}

		setProducts(newProducts);
	}, [responseProducts, data.estate_id]);

	useEffect(() => {
		if (response && response.objectives) {
			setObjectives(response.objectives);
		}
	}, [response]);

	const handleAddItem = () => {
		const newItems = [...data.items];

		newItems.push({ ...baseItem });

		const newData = { ...data, items: newItems.filter((item) => Boolean(item.isCopy) === false) };

		calculateTotal({ newData });
	};

	const updateItemsAtIndex = (items, i, patch) => {
		const real = items.filter((item) => !item.isCopy);
		const copy = items.filter((item) => item.isCopy);

		if (real[i]) real[i] = { ...real[i], ...patch };
		if (copy[i]) copy[i] = { ...copy[i], ...patch };

		return [...real, ...copy];
	};

	const changeProduct = async (i, element) => {
		const value = element?.value ?? "";
		const selected = products.find((p) => Number(p.value) === Number(value));

		const patch = {
			product_id: value,
			product_code: selected?.code,
			product_name: selected?.label,
			tradename: element?.label,
			color_tag: selected?.color_tag,
			reingreso: selected?.reingreso,
			active_ingredient: selected?.active_ingredient,
			active_ingredients: selected?.active_ingredients,
			aptitudes: selected?.aptitudes,
			unit_measurement: selected?.unit_measurement,
			url_tag: selected?.url_tag,
		};

		const businessCenterIds = data.business_centers.map((bc) => bc.value);
		if (value && businessCenterIds.length > 0) {
			try {
				const response = await getProductLackDays({
					product_id: value,
					business_center_ids: businessCenterIds,
				});

				if (response?.lack_days !== undefined) {
					patch.lack_days = response.lack_days;
				}
			} catch (error) {
				console.error("Error al obtener días de carencia:", error);
			}
		}

		setData((prev) => ({
			...prev,
			items: updateItemsAtIndex(prev.items, i, patch),
		}));
	};

	const changeObjective = (i, elem, meta) => {
		const value = elem?.value ?? "";
		const patch = {
			application_orders_objetives_id: value,
			application_orders_objetives_name: elem?.label,
		};

		setData((prev) => ({
			...prev,
			items: updateItemsAtIndex(prev.items, i, patch),
		}));

		if (meta?.action === "create-option") {
			setObjectives((prev) => {
				const exists = prev.some((o) => String(o.value) === String(value));
				return exists ? prev : [...prev, { value, label: elem?.label ?? value }];
			});
		}
	};

	const changeLackDays = (i, value) => {
		setData((prev) => ({
			...prev,
			items: updateItemsAtIndex(prev.items, i, { lack_days: value }),
		}));
	};

	const changeDoseX100Ml = (i, value) => {
		setData((prev) => {
			const newData = {
				...prev,
				items: updateItemsAtIndex(prev.items, i, { dose_100L: value }),
			};

			const filteredData = {
				...newData,
				items: newData.items.filter((item) => Boolean(item.isCopy) === false),
			};

			calculateTotal({ newData: filteredData, type: "dose_100L" });

			return newData;
		});
	};

	const changeDoseHa = (i, value) => {
		setData((prev) => {
			const newData = {
				...prev,
				items: updateItemsAtIndex(prev.items, i, { dose_ha: value }),
			};
			return newData;
		});

		setData((prev) => {
			const filteredData = {
				...prev,
				items: prev.items.filter((item) => Boolean(item.isCopy) === false),
			};
			calculateTotal({ newData: filteredData, type: "dose_ha" });
			return prev;
		});
	};

	const hasInvalidClone = (newData, response) => {
		if (!newData || !response) return true;

		const totalArea = newData.business_centers.reduce((sum, item) => {
			const parsed = Number(String(item.area_select).replace(",", "."));
			return sum + (isNaN(parsed) ? 0 : parsed);
		}, 0);

		const wetness = newData.machineries.length !== 0 ? newData.machineries[0].wetness : 0;
		const machined = newData.machineries.length !== 0 ? newData.machineries[0].machined : 0;

		return newData.items
			.filter((item) => Boolean(item.isCopy) === false)
			.some((item) => {
				const dose_100L = parseFloat(item.dose_100L) || 0;
				const dose_machinery = (dose_100L * machined) / 1000 / 100;
				const quantity_machinery = (totalArea * wetness) / machined;

				const decimalPart = quantity_machinery % 1;
				const quantityDecimal = decimalPart * dose_machinery;

				const rounded = Math.round(quantityDecimal * 100) / 100;

				return rounded === 0;
			});
	};

	const totalArea = data.business_centers.reduce((sum, item) => {
		const parsed = Number(String(item.area_select).replace(",", "."));
		return sum + (isNaN(parsed) ? 0 : parsed);
	}, 0);

	const nameMachinery = `${data.machineries[0]?.machinery_name || ""}, ${data.machineries[0]?.tractor_name || ""} con maquinada de `;

	return (
		<Col md={12} className={classes.col_base}>
			<Card className={activeProduct && "active"}>
				<Card.Header onClick={() => setActiveProduct(!activeProduct)} className={activeProduct && "active"}>
					Productos
					<div>
						<span style={{ marginRight: "1em" }}>
							<strong> Total Ha {Number(data.application_order_type_id) === 2 && " Sector de Riego"}: </strong>{" "}
							{totalArea > 0 ? formatMilesDecimalNew(String(totalArea).replace(".", ",")) : ""}
						</span>
						<span>
							<strong> Cant. de Productos:</strong> {data.items.length ? data.items.length : 0}
						</span>
					</div>
				</Card.Header>

				{activeProduct &&
					(loadingProducts ? (
						<Fetching topMargin={"2em"} bottomMargin={"2em"} message="Cargando productos..." />
					) : (
						<>
							<Card.Body>
								<ProductSelectorDB
									value={data.all_product}
									onChange={(newValue) => setData((prevData) => ({ ...prevData, all_product: newValue }))}
									isFetching={loadingProducts}
								/>

								{!!data.machinery_integer && !!data.show_division && (
									<h4 style={{ fontSize: "16px", textDecoration: "underline" }}>{`${nameMachinery} ${formatMiles(
										round(data.items.find((item) => Boolean(item.isCopy) === false)?.machined || 0),
									)} litros.`}</h4>
								)}

								<CreateAppOrderTableHead data={data} />
								<Row className={classes.body_table}>
									<Col md={12} sm={12} style={{ marginBottom: "4em" }}>
										<CreateAppOrderTableItem
											data={data}
											products={products}
											objectives={objectives}
											changeProduct={changeProduct}
											changeObjective={changeObjective}
											changeLackDays={changeLackDays}
											changeDoseX100Ml={changeDoseX100Ml}
											changeDoseHa={changeDoseHa}
											isCopy={false}
											calculateTotal={calculateTotal}
											setData={setData}
											loadingProducts={loadingProducts}
										/>
									</Col>
								</Row>

								{!!data.machinery_integer && (
									<>
										{!!data.show_division && (
											<h4 style={{ fontSize: "16px", textDecoration: "underline" }}>{`${nameMachinery} ${formatMiles(
												round(data.items.find((item) => Boolean(item.isCopy) === true)?.machined || 0),
											)} litros.`}</h4>
										)}

										<CreateAppOrderTableHead />
										<Row className={classes.body_table}>
											<Col md={12} sm={12} style={{ marginBottom: "4em" }}>
												<CreateAppOrderTableItem
													data={data}
													products={products}
													objectives={objectives}
													changeProduct={changeProduct}
													changeObjective={changeObjective}
													changeLackDays={changeLackDays}
													changeDoseX100Ml={changeDoseX100Ml}
													changeDoseHa={changeDoseHa}
													isCopy={true}
													calculateTotal={calculateTotal}
												/>
											</Col>
										</Row>
									</>
								)}
							</Card.Body>
							<Card.Footer>
								<AddIcon className={classes.iconAdd} onClick={() => handleAddItem()} />
								{[1].includes(Number(data.application_order_type_id)) && data.application_orders_state_id !== "5" && (
									<div className={classes.div_calculate_machined}>
										<Button
											className={`btn bg-agro-outline float-right ${data.machinery_integer && "btn-gray"}`}
											onClick={() => {
												if (!data.machinery_integer && data.items.length !== 0 && data.items[0].quantity_machinery % 1 === 0) {
													return;
												}

												const newData = {
													...data,
													machinery_integer: !data.machinery_integer,
												};

												if (newData.machinery_integer && hasInvalidClone(newData, response)) {
													addToast(
														"No se puede calcular con maquinadas enteras, ya que al menos un producto tendrá gasto total en 0.",
														{
															appearance: "error",
															autoDismiss: true,
														},
													);
													return;
												}

												calculateTotal({ newData });
											}}
											style={{ width: "350px" }}
										>
											{!data.machinery_integer ? "Calcular con maquinadas enteras" : "Calcular con maquinadas originales"}
										</Button>

										{Number(data.machinery_integer) === 0 && data.items.length !== 0 && data.items[0].quantity_machinery < 1 && (
											<span className={classes.text_calculate_machined}>
												Para calcular las Maquinadas (Un), se editará el valor de maquinada (L)
											</span>
										)}
									</div>
								)}
							</Card.Footer>
						</>
					))}
			</Card>
		</Col>
	);
};

export default CreateAppOrderTableDetail;
